import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Grid,
  CircularProgress,
  Autocomplete,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import { JuntoApiService, Booking, User, Class, Credit, Membership } from '../services/api';

const BookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    booking: Booking | null;
  }>({ open: false, booking: null });
  const [filters, setFilters] = useState({
    status: '',
    user_id: '',
    class_id: ''
  });

  // Create Booking Modal State
  const [createBookingDialog, setCreateBookingDialog] = useState(false);
  const [createBookingLoading, setCreateBookingLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [credits, setCredits] = useState<Credit[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('membership');
  const [userWallet, setUserWallet] = useState<any>(null);
  const [specialRequests, setSpecialRequests] = useState('');

  // Data for display
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allClasses, setAllClasses] = useState<Class[]>([]);

  // User Info Modal
  const [userInfoDialog, setUserInfoDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  // Revisions Modal
  const [revisionsDialog, setRevisionsDialog] = useState<{
    open: boolean;
    booking: Booking | null;
    revisions: any[];
  }>({ open: false, booking: null, revisions: [] });

  useEffect(() => {
    loadBookings();
  }, [filters]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const [bookingsData, usersData, classesData] = await Promise.all([
        JuntoApiService.getBookings(true), // TODO: API doesn't support filters yet
        JuntoApiService.getUsers(),
        JuntoApiService.getClasses()
      ]);
      setBookings(bookingsData);
      setAllUsers(usersData);
      setAllClasses(classesData);
      
      // Check if bookings is empty due to backend issue
      if (bookingsData.length === 0) {
        console.warn('No bookings data received. This may be due to a backend issue with the GET /api/v2/bookings endpoint.');
      }
    } catch (err) {
      setError('Failed to load bookings');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (booking: Booking) => {
    setCancelDialog({ open: true, booking });
  };

  const confirmCancelBooking = async () => {
    if (!cancelDialog.booking) return;

    try {
      await JuntoApiService.cancelBooking(
        cancelDialog.booking.id,
        'admin-user' // TODO: Get actual admin user ID from auth context
      );
      setCancelDialog({ open: false, booking: null });
      loadBookings(); // Reload data
    } catch (err) {
      setError('Failed to cancel booking');
      console.error('Error cancelling booking:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'waitlisted':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'primary';
      case 'no_show':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper functions
  const getUserName = (userId: string): string => {
    const user = allUsers.find(u => u.id === userId);
    return user ? user.name : userId;
  };

  const getUser = (userId: string): User | null => {
    return allUsers.find(u => u.id === userId) || null;
  };

  const getClassName = (classId: string): string => {
    const classItem = allClasses.find(c => c.id === classId);
    return classItem ? classItem.name : classId;
  };

  // Modal handlers
  const handleUserClick = (userId: string) => {
    const user = getUser(userId);
    if (user) {
      setUserInfoDialog({ open: true, user });
    }
  };

  const handleRevisionsClick = async (booking: Booking) => {
    setRevisionsDialog({ open: true, booking, revisions: [] });
    
    // TODO: Add API call to fetch booking revisions/history
    // For now, create mock revision data
    const mockRevisions = [
      {
        timestamp: booking.created_at,
        status: 'confirmed',
        action: 'Booking created',
        by: 'System'
      },
      {
        timestamp: booking.updated_at,
        status: booking.status,
        action: `Status changed to ${booking.status}`,
        by: 'Admin'
      }
    ];
    
    setRevisionsDialog(prev => ({ ...prev, revisions: mockRevisions }));
  };

  // Create Booking Functions
  const openCreateBookingDialog = async () => {
    setCreateBookingDialog(true);
    setCreateBookingLoading(true);
    
    try {
      // Load all required data
      const [usersData, classesData, creditsData, membershipsData] = await Promise.all([
        JuntoApiService.getUsers(),
        JuntoApiService.getClasses(),
        JuntoApiService.getCredits(),
        JuntoApiService.getMemberships()
      ]);
      
      setUsers(usersData);
      setClasses(classesData);
      setCredits(creditsData);
      setMemberships(membershipsData);
    } catch (err) {
      setError('Failed to load data for booking creation');
      console.error('Error loading create booking data:', err);
    } finally {
      setCreateBookingLoading(false);
    }
  };

  const handleUserChange = async (user: User | null) => {
    setSelectedUser(user);
    setUserWallet(null);
    
    if (user) {
      try {
        const wallet = await JuntoApiService.getUserWallet(user.id);
        setUserWallet(wallet);
      } catch (err) {
        console.error('Error loading user wallet:', err);
      }
    }
  };

  const getAvailablePaymentMethods = () => {
    const methods = [];
    
    if (selectedUser && userWallet) {
      // Check for active memberships
      if (userWallet.memberships && userWallet.memberships.length > 0) {
        const activeMemberships = userWallet.memberships.filter((m: any) => 
          m.is_active && new Date(m.end_date) > new Date()
        );
        if (activeMemberships.length > 0) {
          methods.push({ value: 'membership', label: `Membership (${activeMemberships.length} active)` });
        }
      }
      
      // Check for available credits
      if (userWallet.credits && userWallet.credits.length > 0) {
        const totalCredits = userWallet.credits.reduce((sum: number, c: any) => sum + c.quantity, 0);
        if (totalCredits > 0) {
          methods.push({ value: 'credit', label: `Credits (${totalCredits} available)` });
        }
      }
      
      // Cash balance
      if (userWallet.cash_balance && userWallet.cash_balance > 0) {
        methods.push({ value: 'cash', label: `Cash Balance ($${userWallet.cash_balance})` });
      }
    }
    
    // Always available options
    methods.push({ value: 'card', label: 'Charge Card' });
    methods.push({ value: 'comp', label: 'Comp (Free)' });
    
    return methods;
  };

  const createBooking = async () => {
    if (!selectedUser || !selectedClass) {
      setError('Please select both a user and a class');
      return;
    }

    setCreateBookingLoading(true);
    try {
      const bookingData = {
        user_id: selectedUser.id,
        class_id: selectedClass.id,
        status: 'confirmed' as const,
        booked_at: new Date().toISOString(),
        payment_id: paymentMethod === 'comp' ? 'comp-admin' : `${paymentMethod}-${Date.now()}`,
        special_requests: specialRequests || `Admin booking - Payment: ${paymentMethod}`
      };

      await JuntoApiService.createBooking(
        bookingData,
        'admin-user' // TODO: Get actual admin user ID from auth context
      );
      
      // Reset form and close dialog
      setCreateBookingDialog(false);
      setSelectedUser(null);
      setSelectedClass(null);
      setPaymentMethod('membership');
      setSpecialRequests('');
      setUserWallet(null);
      
      // Reload bookings
      loadBookings();
      
    } catch (err) {
      setError('Failed to create booking');
      console.error('Error creating booking:', err);
    } finally {
      setCreateBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading bookings...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Bookings Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateBookingDialog}
        >
          Create Booking
        </Button>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: '1rem' }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={1} style={{ padding: '1rem', marginBottom: '2rem' }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="waitlisted">Waitlisted</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="no_show">No Show</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="User ID"
            value={filters.user_id}
            onChange={(e) => handleFilterChange('user_id', e.target.value)}
            style={{ minWidth: 200 }}
          />
          
          <TextField
            label="Class ID"
            value={filters.class_id}
            onChange={(e) => handleFilterChange('class_id', e.target.value)}
            style={{ minWidth: 200 }}
          />
        </Box>
      </Paper>

      {/* Bookings Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Booking ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Booked At</TableCell>
              <TableCell>Special Requests</TableCell>
              <TableCell align="center">Rev</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map((booking) => (
              <TableRow key={booking.id}>
                <TableCell>
                  <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                    {booking.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    variant="text"
                    startIcon={<PersonIcon />}
                    onClick={() => handleUserClick(booking.user_id)}
                    sx={{ textTransform: 'none' }}
                  >
                    {getUserName(booking.user_id)}
                  </Button>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getClassName(booking.class_id)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={booking.status}
                    color={getStatusColor(booking.status) as any}
                    size="small"
                  />
                  {booking.waitlist_position && (
                    <Typography variant="caption" display="block">
                      Position: {booking.waitlist_position}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(booking.booked_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  {booking.special_requests || '-'}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleRevisionsClick(booking)}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    <HistoryIcon fontSize="small" />
                  </Button>
                </TableCell>
                <TableCell>
                  {booking.status === 'confirmed' && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleCancelBooking(booking)}
                    >
                      Cancel
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {bookings.length === 0 && !loading && (
        <Paper elevation={1} style={{ padding: '2rem', textAlign: 'center', marginTop: '2rem' }}>
          <Typography variant="h6" color="textSecondary">
            No bookings found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {error ? 
              'There was an error loading bookings. This may be due to a backend issue with the GET /api/v2/bookings endpoint.' :
              'Try adjusting your filters or check back later.'
            }
          </Typography>
          {error && (
            <Typography variant="body2" color="error" style={{ marginTop: '1rem' }}>
              Technical Note: The backend may be missing the proper implementation for the documented GET /api/v2/bookings endpoint.
            </Typography>
          )}
        </Paper>
      )}

      {/* Cancel Booking Dialog */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, booking: null })}
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this booking?
          </Typography>
          {cancelDialog.booking && (
            <Box mt={2}>
              <Typography variant="body2">
                <strong>Booking ID:</strong> {cancelDialog.booking.id}
              </Typography>
              <Typography variant="body2">
                <strong>User:</strong> {cancelDialog.booking.user_id}
              </Typography>
              <Typography variant="body2">
                <strong>Class:</strong> {cancelDialog.booking.class_id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, booking: null })}>
            Cancel
          </Button>
          <Button onClick={confirmCancelBooking} color="error" variant="contained">
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Booking Dialog */}
      <Dialog
        open={createBookingDialog}
        onClose={() => setCreateBookingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Booking</DialogTitle>
        <DialogContent>
          {createBookingLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Grid container spacing={3}>
                {/* User Selection */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => `${option.name} (${option.email})`}
                    value={selectedUser}
                    onChange={(event, newValue) => handleUserChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select User"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                  />
                </Grid>

                {/* Class Selection */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={classes}
                    getOptionLabel={(option) => `${option.name} - ${new Date(option.start_time).toLocaleString()}`}
                    value={selectedClass}
                    onChange={(event, newValue) => setSelectedClass(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Class"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                  />
                </Grid>

                {/* Payment Method Selection */}
                {selectedUser && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Payment Method</FormLabel>
                      <RadioGroup
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        {getAvailablePaymentMethods().map((method) => (
                          <FormControlLabel
                            key={method.value}
                            value={method.value}
                            control={<Radio />}
                            label={method.label}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                )}

                {/* User Wallet Summary */}
                {selectedUser && userWallet && (
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h6" gutterBottom>
                        User Wallet Summary
                      </Typography>
                      <Typography variant="body2">
                        <strong>Cash Balance:</strong> ${userWallet.cash_balance || 0}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Credits:</strong> {userWallet.credits ? userWallet.credits.reduce((sum: number, c: any) => sum + c.quantity, 0) : 0}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Active Memberships:</strong> {userWallet.memberships ? userWallet.memberships.filter((m: any) => m.is_active && new Date(m.end_date) > new Date()).length : 0}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Special Requests */}
                <Grid item xs={12}>
                  <TextField
                    label="Special Requests / Notes"
                    multiline
                    rows={3}
                    fullWidth
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="Any special requests or admin notes..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBookingDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={createBooking}
            variant="contained"
            color="primary"
            disabled={!selectedUser || !selectedClass || createBookingLoading}
          >
            {createBookingLoading ? 'Creating...' : 'Create Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Info Modal */}
      <Dialog
        open={userInfoDialog.open}
        onClose={() => setUserInfoDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Information</DialogTitle>
        <DialogContent>
          {userInfoDialog.user && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {userInfoDialog.user.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {userInfoDialog.user.email}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Phone:</strong> {userInfoDialog.user.phone || 'Not provided'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Date of Birth:</strong> {userInfoDialog.user.date_of_birth || 'Not provided'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Emergency Contact:</strong> {userInfoDialog.user.emergency_contact_name || 'Not provided'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Emergency Phone:</strong> {userInfoDialog.user.emergency_contact_phone || 'Not provided'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Member Since:</strong> {new Date(userInfoDialog.user.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserInfoDialog({ open: false, user: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revisions Modal */}
      <Dialog
        open={revisionsDialog.open}
        onClose={() => setRevisionsDialog({ open: false, booking: null, revisions: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Booking Revisions History</DialogTitle>
        <DialogContent>
          {revisionsDialog.booking && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Booking: {revisionsDialog.booking.id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                User: {getUserName(revisionsDialog.booking.user_id)} | 
                Class: {getClassName(revisionsDialog.booking.class_id)}
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Status History:
              </Typography>
              
              {revisionsDialog.revisions.length > 0 ? (
                revisionsDialog.revisions.map((revision, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Date:</strong> {new Date(revision.timestamp).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Action:</strong> {revision.action}
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" component="span">
                        <strong>Status:</strong>
                      </Typography>
                      <Chip
                        label={revision.status}
                        color={getStatusColor(revision.status) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    <Typography variant="body2">
                      <strong>By:</strong> {revision.by}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No revision history available
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionsDialog({ open: false, booking: null, revisions: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingsPage;